import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/apiAuth';
import CONSTANTS, { ContentTypeConstants } from '../constants/contentCreationConstants';
import { Box, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Typography, Chip, Stack } from '@mui/material';

const InlineHierarchicalFilter = ({
  projects = [],
  initialProjectId,
  initialFilters = {},
  onApply,
  onProjectChange
}) => {
  const [data, setData] = useState({ projects: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState({
    project: initialProjectId || '',
    approvalStatus: initialFilters.approvalStatus || 'All',
    contentType: initialFilters.contentType || ContentTypeConstants.VIDEO.key,
    subject: initialFilters.subject || '',
    topic: initialFilters.topic || '',
    subTopic: initialFilters.subTopic || ''
  });

  useEffect(() => {
    fetchAllHierarchicalFilters();
  }, []);

  const fetchAllHierarchicalFilters = async () => {
    try {
      setLoading(true);
      const resp = await apiGet('/api/hierarchical-filters');
      const payload = resp.data || resp;
      setData(payload);
    } catch (err) {
      setError('Failed to load filter options');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentProjectData = () => {
    const pid = selected.project || initialProjectId;
    return (data.projects || []).find(p => Number(p.project_id) === Number(pid)) || null;
  };

  const getSubjects = () => getCurrentProjectData()?.subjects || [];
  const getTopicsForSubject = () => {
    const subjects = getSubjects();
    if (!selected.subject) return [];
    const subj = subjects.find(s => s.subject_id === selected.subject);
    return subj?.topics || [];
  };
  const getSubTopicsForTopic = () => {
    const topics = getTopicsForSubject();
    if (!selected.topic) return [];
    const top = topics.find(t => t.topic_id === selected.topic);
    return top?.sub_topics || [];
  };

  const handleChange = (key, value) => {
    // Clear dependent selections when parent changes
    const updates = { ...selected, [key]: value };
    if (key === 'project') {
      updates.subject = '';
      updates.topic = '';
      updates.subTopic = '';
      if (onProjectChange) onProjectChange(value);
    }
    if (key === 'approvalStatus') {
      // no-op
    }
    if (key === 'subject') {
      updates.topic = '';
      updates.subTopic = '';
    }
    if (key === 'topic') {
      updates.subTopic = '';
    }
    setSelected(updates);
  };

  const handleApply = () => {
    if (onApply) {
      onApply({
        project: selected.project || initialProjectId,
        approvalStatus: selected.approvalStatus,
        contentType: selected.contentType,
        subject: selected.subject,
        topic: selected.topic,
        subTopic: selected.subTopic
      });
    }
  };

  const approvalStatuses = (() => {
    const p = getCurrentProjectData();
    return (p && p.approval_statuses && p.approval_statuses.length) ? p.approval_statuses : ['selected','uploaded','assigned','approved'];
  })();

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Project selector */}
      <FormControl sx={{ minWidth: 180 }}>
        <InputLabel>Project</InputLabel>
        <Select
          value={selected.project || ''}
          label="Project"
          onChange={(e) => handleChange('project', e.target.value)}
        >
          <MenuItem value="">Select Project</MenuItem>
          {(data.projects || projects || []).map(p => (
            <MenuItem key={p.project_id || p.id} value={p.project_id || p.id}>{p.project_name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      

      {/* Subject */}
      <FormControl sx={{ minWidth: 200 }} disabled={!selected.project}>
        <InputLabel>Subject</InputLabel>
        <Select
          value={selected.subject}
          label="Subject"
          onChange={(e) => handleChange('subject', e.target.value)}
        >
          <MenuItem value="">Select Subject</MenuItem>
          {getSubjects().map(s => (
            <MenuItem key={s.subject_id} value={s.subject_id}>{s.subject_name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Topic */}
      <FormControl sx={{ minWidth: 220 }} disabled={!selected.subject}>
        <InputLabel>Topic</InputLabel>
        <Select
          value={selected.topic}
          label="Topic"
          onChange={(e) => handleChange('topic', e.target.value)}
        >
          <MenuItem value="">Select Topic</MenuItem>
          {getTopicsForSubject().map(t => (
            <MenuItem key={t.topic_id} value={t.topic_id}>{t.topic_name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* SubTopic */}
      <FormControl sx={{ minWidth: 220 }} disabled={!selected.topic}>
        <InputLabel>Sub-Topic</InputLabel>
        <Select
          value={selected.subTopic}
          label="Sub-Topic"
          onChange={(e) => handleChange('subTopic', e.target.value)}
        >
          <MenuItem value="">Select Sub-Topic</MenuItem>
          {getSubTopicsForTopic().map(st => (
            <MenuItem key={st.sub_topic_id} value={st.sub_topic_id}>{st.sub_topic_name}</MenuItem>
          ))}
        </Select>
      </FormControl>



      {/* Approval Status */}
      <FormControl sx={{ minWidth: 160 }}>
        <InputLabel>Approval Status</InputLabel>
        <Select
          value={selected.approvalStatus}
          label="Approval Status"
          onChange={(e) => handleChange('approvalStatus', e.target.value)}
        >
          <MenuItem value="All">All</MenuItem>
          {approvalStatuses.map(s => (
            <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
          ))}
        </Select>
      </FormControl>


      {/* Content Type */}
      <FormControl sx={{ minWidth: 160 }}>
        <InputLabel>Content Type</InputLabel>
        <Select
          value={selected.contentType}
          label="Content Type"
          onChange={(e) => handleChange('contentType', e.target.value)}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value={ContentTypeConstants.VIDEO.key}>Video</MenuItem>
          <MenuItem value={ContentTypeConstants.NOTES.key}>Notes</MenuItem>
          <MenuItem value={ContentTypeConstants.MCQ.key}>MCQ</MenuItem>
          <MenuItem value={ContentTypeConstants.CODING.key}>Coding</MenuItem>
          <MenuItem value={ContentTypeConstants.PROJECT_CODING.key}>Project Coding</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {/* <Button variant="outlined" onClick={() => {
          // Reset to defaults
          setSelected({
            project: initialProjectId || '',
            approvalStatus: 'All',
            contentType: ContentTypeConstants.VIDEO.key,
            subject: '',
            topic: '',
            subTopic: ''
          });
        }}>Reset</Button> */}
        <Button variant="contained" onClick={handleApply} sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}>
          Apply
        </Button>
      </Box>

      {loading && <CircularProgress size={20} />}
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default InlineHierarchicalFilter;

